import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarMonthIcon,
  Collections as CollectionsIcon,
  FolderOpen as FolderOpenIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useParams } from "react-router-dom";
import api from "../api/api";

function resolveMediaType(value) {
  const normalizedValue = String(value || "").toLowerCase();

  if (normalizedValue.includes("video")) {
    return "video";
  }

  return "image";
}

function normalizeBoolean(value, fallback = true) {
  if ([true, 1, "1", "true"].includes(value)) {
    return true;
  }

  if ([false, 0, "0", "false"].includes(value)) {
    return false;
  }

  return fallback;
}

function normalizeAlbum(rawAlbum) {
  return {
    id: rawAlbum?.AlbumId || rawAlbum?.albumId || rawAlbum?.id,
    title: rawAlbum?.AlbumTitle || rawAlbum?.albumTitle || "",
    description: rawAlbum?.AlbumDescription || rawAlbum?.albumDescription || "",
    eventDate: (rawAlbum?.EventDate || rawAlbum?.eventDate || "").slice(0, 10),
    coverImage:
      rawAlbum?.CoverImage ||
      rawAlbum?.coverImage ||
      rawAlbum?.CoverImageUrl ||
      rawAlbum?.coverImageUrl ||
      "",
    parentFolderId:
      rawAlbum?.ParentFolderId ?? rawAlbum?.parentFolderId ?? rawAlbum?.parentId ?? null,
    isActive: normalizeBoolean(rawAlbum?.IsActive ?? rawAlbum?.isActive, true),
    imagesCount: Number(rawAlbum?.ImagesCount || rawAlbum?.imagesCount || 0),
  };
}

function normalizeImage(rawImage) {
  return {
    id:
      rawImage?.GalleryImageId || rawImage?.galleryImageId || rawImage?.ImageId,
    title: rawImage?.ImageTitle || rawImage?.imageTitle || "",
    description: rawImage?.ImageDescription || rawImage?.imageDescription || "",
    imageUrl:
      rawImage?.ImageUrl ||
      rawImage?.imageUrl ||
      rawImage?.ImagePath ||
      rawImage?.imagePath ||
      rawImage?.FilePath ||
      rawImage?.filePath ||
      rawImage?.Url ||
      rawImage?.url ||
      "",
    imageType: resolveMediaType(rawImage?.ImageType || rawImage?.imageType),
    displayOrder: Number(rawImage?.DisplayOrder ?? rawImage?.displayOrder ?? 0),
    isActive: normalizeBoolean(rawImage?.IsActive ?? rawImage?.isActive, true),
  };
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function extractAlbums(payload) {
  return [
    ...toArray(payload?.albums),
    ...toArray(payload?.data?.albums),
    ...toArray(payload?.galleryAlbums),
    ...toArray(payload?.data?.galleryAlbums),
    ...(Array.isArray(payload) ? payload : []),
  ];
}

function extractAlbum(payload) {
  return (
    payload?.album ||
    payload?.data?.album ||
    payload?.galleryAlbum ||
    payload?.data?.galleryAlbum ||
    payload?.data ||
    payload
  );
}

function extractImages(payload) {
  return [
    ...toArray(payload?.images),
    ...toArray(payload?.data?.images),
    ...toArray(payload?.galleryImages),
    ...toArray(payload?.data?.galleryImages),
    ...toArray(payload?.album?.images),
    ...toArray(payload?.album?.galleryImages),
    ...toArray(payload?.album?.GalleryImages),
    ...toArray(payload?.data?.album?.images),
    ...toArray(payload?.data?.album?.galleryImages),
    ...toArray(payload?.galleryAlbum?.images),
    ...toArray(payload?.galleryAlbum?.galleryImages),
  ];
}

function extractChildAlbums(payload) {
  return [
    ...toArray(payload?.folders),
    ...toArray(payload?.data?.folders),
    ...toArray(payload?.album?.albums),
    ...toArray(payload?.album?.folders),
    ...toArray(payload?.album?.galleryAlbums),
    ...toArray(payload?.album?.GalleryAlbums),
    ...toArray(payload?.data?.album?.albums),
    ...toArray(payload?.data?.album?.folders),
    ...toArray(payload?.data?.album?.galleryAlbums),
    ...toArray(payload?.galleryAlbum?.albums),
    ...toArray(payload?.galleryAlbum?.folders),
    ...toArray(payload?.galleryAlbum?.galleryAlbums),
    ...toArray(payload?.galleryAlbum?.GalleryAlbums),
    ...toArray(payload?.data?.galleryAlbum?.albums),
    ...toArray(payload?.data?.galleryAlbum?.folders),
    ...toArray(payload?.data?.galleryAlbum?.galleryAlbums),
  ];
}

function getErrorMessage(error, fallbackMessage) {
  const data = error?.response?.data;

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data?.message) {
    return data.message;
  }

  return error?.message || fallbackMessage;
}

function formatDate(dateString) {
  if (!dateString) {
    return "Date not specified";
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "Date not specified";
  }

  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function GalleryEmptyState({ title, description }) {
  return (
    <Box
      sx={{
        py: 8,
        px: 3,
        borderRadius: 4,
        textAlign: "center",
        border: "1px dashed rgba(15, 23, 42, 0.16)",
        bgcolor: "rgba(248, 250, 252, 0.9)",
      }}
    >
      <CollectionsIcon sx={{ fontSize: 40, color: "#64748b", mb: 1.5 }} />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography color="text.secondary">{description}</Typography>
    </Box>
  );
}

function GalleryAlbumList() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadAlbums() {
      try {
        setLoading(true);
        setError("");
        const response = await api.getGalleryAlbums({ IsActive: 1 });
        const responseData = response?.data || response || {};
        const albumList = extractAlbums(responseData);

        if (active) {
          setAlbums(
            albumList
              .map(normalizeAlbum)
              .filter(
                (album) =>
                  album.isActive &&
                  !album.parentFolderId,
              ),
          );
        }
      } catch (loadError) {
        if (active) {
          setError(getErrorMessage(loadError, "Unable to load gallery albums."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadAlbums();

    return () => {
      active = false;
    };
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 7 } }}>
      <Stack spacing={4}>
        <Box
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 5,
            color: "white",
            background:
              "linear-gradient(135deg, rgba(3, 105, 161, 0.96) 0%, rgba(15, 23, 42, 0.96) 100%)",
          }}
        >
          <Chip
            label="Public Gallery"
            sx={{
              mb: 2,
              color: "white",
              bgcolor: "rgba(255,255,255,0.14)",
              fontWeight: 700,
            }}
          />
          <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5 }}>
            Community gallery, open to everyone
          </Typography>
          <Typography
            variant="body1"
            sx={{ maxWidth: 720, color: "rgba(255,255,255,0.84)" }}
          >
            Browse top-level albums without signing in. Open any album or folder
            to explore nested folders, images, and videos.
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && !error && albums.length === 0 ? (
          <GalleryEmptyState
            title="No albums available"
            description="Top-level gallery folders will appear here once content is added."
          />
        ) : null}

        {!loading && albums.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 3,
            }}
          >
            {albums.map((album) => (
              <Card
                key={album.id}
                sx={{
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
                }}
              >
                <CardActionArea component={RouterLink} to={`/gallery/${album.id}`}>
                  <Box
                    sx={{
                      position: "relative",
                      height: 260,
                      bgcolor: "#e2e8f0",
                    }}
                  >
                    {album.coverImage ? (
                      <Box
                        component="img"
                        src={album.coverImage}
                        alt={album.title}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background:
                            "linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)",
                        }}
                      >
                        <PhotoLibraryIcon sx={{ fontSize: 56, color: "#2563eb" }} />
                      </Box>
                    )}
                    <Chip
                      label={`${album.imagesCount} items`}
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        bgcolor: "rgba(15, 23, 42, 0.72)",
                        color: "white",
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={1.25}>
                      <Typography variant="h6" fontWeight={800} color="#0f172a">
                        {album.title || "Untitled album"}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center", color: "text.secondary" }}
                      >
                        <CalendarMonthIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2">
                          {formatDate(album.eventDate)}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          minHeight: 40,
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {album.description || "Open this folder to browse its contents."}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        ) : null}
      </Stack>
    </Container>
  );
}

function GalleryAlbumDetail() {
  const { albumId } = useParams();
  const [album, setAlbum] = useState(null);
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [activeTab, setActiveTab] = useState("images");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadAlbum() {
      try {
        setLoading(true);
        setError("");
        const response = await api.getGalleryAlbumById(albumId, { IsActive: 1 });
        const responseData = response?.data || response || {};
        const resolvedAlbum = normalizeAlbum(extractAlbum(responseData));
        const resolvedFolders = extractChildAlbums(responseData).map(normalizeAlbum);
        const resolvedImages = extractImages(responseData).map(normalizeImage);

        if (active) {
          setAlbum(resolvedAlbum?.id ? resolvedAlbum : null);
          setFolders(
            resolvedFolders
              .filter(
                (folder) =>
                  folder.isActive &&
                  folder.id &&
                  String(folder.id) !== String(resolvedAlbum?.id),
              )
              .sort((first, second) => first.title.localeCompare(second.title)),
          );
          setImages(
            resolvedImages
              .filter((image) => image.isActive && image.imageUrl)
              .sort((first, second) => first.displayOrder - second.displayOrder),
          );
        }
      } catch (loadError) {
        if (active) {
          setError(getErrorMessage(loadError, "Unable to load this album."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadAlbum();

    return () => {
      active = false;
    };
  }, [albumId]);

  const imageCountLabel = useMemo(() => {
    const count = images.length;
    return `${count} media item${count === 1 ? "" : "s"}`;
  }, [images]);

  const folderCountLabel = useMemo(() => {
    const count = folders.length;
    return `${count} folder${count === 1 ? "" : "s"}`;
  }, [folders]);

  const imageItems = useMemo(
    () => images.filter((image) => image.imageType !== "video"),
    [images],
  );

  const videoItems = useMemo(
    () => images.filter((image) => image.imageType === "video"),
    [images],
  );

  useEffect(() => {
    function blockEvent(event) {
      event.preventDefault();
    }

    function blockKeyboardShortcuts(event) {
      const key = event.key.toLowerCase();
      const modifierPressed = event.ctrlKey || event.metaKey;

      if (modifierPressed && ["s", "p", "u", "c"].includes(key)) {
        event.preventDefault();
      }

      if (key === "printscreen") {
        event.preventDefault();
      }
    }

    document.addEventListener("contextmenu", blockEvent);
    document.addEventListener("dragstart", blockEvent);
    document.addEventListener("copy", blockEvent);
    document.addEventListener("cut", blockEvent);
    document.addEventListener("selectstart", blockEvent);
    document.addEventListener("keydown", blockKeyboardShortcuts);

    return () => {
      document.removeEventListener("contextmenu", blockEvent);
      document.removeEventListener("dragstart", blockEvent);
      document.removeEventListener("copy", blockEvent);
      document.removeEventListener("cut", blockEvent);
      document.removeEventListener("selectstart", blockEvent);
      document.removeEventListener("keydown", blockKeyboardShortcuts);
    };
  }, []);

  useEffect(() => {
    if (activeTab === "videos" && videoItems.length === 0 && imageItems.length > 0) {
      setActiveTab("images");
      return;
    }

    if (activeTab === "images" && imageItems.length === 0 && videoItems.length > 0) {
      setActiveTab("videos");
    }
  }, [activeTab, imageItems.length, videoItems.length]);

  const visibleItems = activeTab === "videos" ? videoItems : imageItems;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 7 } }}>
      <Stack spacing={4}>
        <Box>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/gallery"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: "none", px: 0, minWidth: 0 }}
            >
              Gallery
            </Button>
            <Typography color="text.primary">
              {album?.title || "Album"}
            </Typography>
          </Breadcrumbs>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && !error && !album ? (
          <GalleryEmptyState
            title="Album not found"
            description="This album is unavailable or has no public content."
          />
        ) : null}

        {!loading && album ? (
          <>
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 5,
                background:
                  "linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(224, 242, 254, 0.98) 100%)",
                border: "1px solid rgba(15, 23, 42, 0.08)",
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="h3" fontWeight={800} color="#0f172a">
                  {album.title || "Untitled album"}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip icon={<FolderOpenIcon />} label={folderCountLabel} />
                  <Chip icon={<CollectionsIcon />} label={imageCountLabel} />
                  <Chip
                    icon={<CalendarMonthIcon />}
                    label={formatDate(album.eventDate)}
                  />
                </Stack>
                <Typography variant="body1" color="text.secondary">
                  {album.description || "Published media from the community gallery."}
                </Typography>
              </Stack>
            </Box>

            {folders.length === 0 && images.length === 0 ? (
              <GalleryEmptyState
                title="This folder is empty"
                description="Folders, images, and videos will appear here once they are published."
              />
            ) : (
              <Stack spacing={3}>
                {folders.length > 0 ? (
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FolderOpenIcon sx={{ color: "#2563eb" }} />
                      <Typography variant="h6" fontWeight={800}>
                        Folders
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 3,
                      }}
                    >
                      {folders.map((folder) => (
                        <Card
                          key={folder.id}
                          sx={{
                            borderRadius: 4,
                            overflow: "hidden",
                            boxShadow: "0 16px 34px rgba(15, 23, 42, 0.08)",
                          }}
                        >
                          <CardActionArea component={RouterLink} to={`/gallery/${folder.id}`}>
                            <Box
                              sx={{
                                minHeight: 220,
                                px: 3,
                                py: 4,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                background:
                                  "linear-gradient(135deg, rgba(224, 242, 254, 0.95) 0%, rgba(219, 234, 254, 0.95) 100%)",
                              }}
                            >
                              <FolderOpenIcon sx={{ fontSize: 72, color: "#2563eb" }} />
                              <Chip
                                label={`${folder.imagesCount} items`}
                                sx={{
                                  position: "absolute",
                                  top: 16,
                                  right: 16,
                                  bgcolor: "rgba(15, 23, 42, 0.72)",
                                  color: "white",
                                  fontWeight: 700,
                                }}
                              />
                            </Box>
                            <CardContent sx={{ p: 3 }}>
                              <Stack spacing={1.25}>
                                <Typography variant="h6" fontWeight={800} color="#0f172a">
                                  {folder.title || "Untitled folder"}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{ alignItems: "center", color: "text.secondary" }}
                                >
                                  <CalendarMonthIcon sx={{ fontSize: 18 }} />
                                  <Typography variant="body2">
                                    {formatDate(folder.eventDate)}
                                  </Typography>
                                </Stack>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    minHeight: 40,
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {folder.description || "Open this folder to continue browsing."}
                                </Typography>
                              </Stack>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))}
                    </Box>
                  </Stack>
                ) : null}

                {images.length > 0 ? (
                <Box
                  sx={{
                    borderRadius: 4,
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    bgcolor: "rgba(255,255,255,0.8)",
                    px: { xs: 1, md: 2 },
                  }}
                >
                  <Tabs
                    value={activeTab}
                    onChange={(_, nextValue) => setActiveTab(nextValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 700,
                        minHeight: 64,
                      },
                    }}
                  >
                    <Tab
                      value="images"
                      label={`Images (${imageItems.length})`}
                      disabled={imageItems.length === 0}
                    />
                    <Tab
                      value="videos"
                      label={`Videos (${videoItems.length})`}
                      disabled={videoItems.length === 0}
                    />
                  </Tabs>
                </Box>
                ) : null}

                {images.length > 0 && visibleItems.length === 0 ? (
                  <GalleryEmptyState
                    title={activeTab === "videos" ? "No videos in this album" : "No images in this album"}
                    description={
                      activeTab === "videos"
                        ? "Videos will appear here once they are published."
                        : "Images will appear here once they are published."
                    }
                  />
                ) : null}

                {visibleItems.length > 0 ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 3,
                    }}
                  >
                    {visibleItems.map((image, index) => (
                      <Card
                        key={image.id || `${image.imageUrl}-${index}`}
                        sx={{
                          borderRadius: 4,
                          overflow: "hidden",
                          boxShadow: "0 16px 34px rgba(15, 23, 42, 0.08)",
                          userSelect: "none",
                          WebkitUserSelect: "none",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            bgcolor: "#0f172a",
                          }}
                        >
                          {image.imageType === "video" ? (
                            <Box
                              component="video"
                              src={image.imageUrl}
                              controls
                              controlsList="nodownload noplaybackrate"
                              disablePictureInPicture
                              preload="metadata"
                              onContextMenu={(event) => event.preventDefault()}
                              sx={{
                                width: "100%",
                                height: 320,
                                objectFit: "cover",
                                display: "block",
                                userSelect: "none",
                                WebkitTouchCallout: "none",
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={image.imageUrl}
                              alt={image.title || `${album.title} image ${index + 1}`}
                              draggable={false}
                              onContextMenu={(event) => event.preventDefault()}
                              onDragStart={(event) => event.preventDefault()}
                              sx={{
                                width: "100%",
                                height: 320,
                                objectFit: "cover",
                                display: "block",
                                pointerEvents: "none",
                                userSelect: "none",
                                WebkitUserDrag: "none",
                                WebkitTouchCallout: "none",
                              }}
                            />
                          )}
                          <Box
                            aria-hidden="true"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              pointerEvents: "none",
                              overflow: "hidden",
                              background:
                                "repeating-linear-gradient(-35deg, rgba(15, 23, 42, 0.08) 0px, rgba(15, 23, 42, 0.08) 22px, rgba(255, 255, 255, 0.02) 22px, rgba(255, 255, 255, 0.02) 44px)",
                            }}
                          >
                            <Typography
                              sx={{
                                px: 2,
                                textAlign: "center",
                                fontSize: { xs: 22, md: 28 },
                                fontWeight: 800,
                                letterSpacing: "0.26em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.32)",
                                textShadow: "0 2px 12px rgba(15, 23, 42, 0.4)",
                                transform: "rotate(-24deg)",
                              }}
                            >
                              Einstein Seeds
                            </Typography>
                          </Box>
                        </Box>
                        {image.title || image.description ? (
                          <CardContent sx={{ p: 2.25 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {image.title ||
                                  `${image.imageType === "video" ? "Video" : "Image"} ${index + 1}`}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={image.imageType === "video" ? "Video" : "Image"}
                              />
                            </Stack>
                            {image.description ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                {image.description}
                              </Typography>
                            ) : null}
                          </CardContent>
                        ) : null}
                      </Card>
                    ))}
                  </Box>
                ) : null}
              </Stack>
            )}
          </>
        ) : null}
      </Stack>
    </Container>
  );
}

function Gallery() {
  const { albumId } = useParams();

  if (albumId) {
    return <GalleryAlbumDetail />;
  }

  return <GalleryAlbumList />;
}

export default Gallery;
